/**
 * AI 패널·AI 메뉴가 한 턴씩 부르는 클라이언트 (설계 판 B).
 *
 * ⚠ `apiClient`(axios)는 못 쓴다 — `timeout: 30_000` 이라 한 턴을 못 태우고 스트림도 못 읽는다.
 * ⚠ `EventSource` 는 레포 금지(쿠키·헤더 제어 불가). `fetch` + `credentials:'include'` 로 읽는다.
 *   오피스 AI(`api/office-ai.ts`)가 같은 이유로 같은 모양이다.
 *
 * 서버가 흘려보내는 것 — `event: ping | delta | complete | error`.
 */

/** 승인을 기다리는 쓰기 한 건. 화면이 카드로 그리고 `continueAssistTurn` 으로 답한다. */
export interface AssistConfirm {
  tool: string;
  input: Record<string, unknown>;
  whatWillHappen: string;
  severity: string;
  blastRadius: string;
  continuationId: string;
  confirmToken: string;
  expiresAt: string;
}

export interface AssistCallbacks {
  /**
   * 이어 갈 대화가 정해졌다 — 새 대화였으면 **여기서 처음** id 를 안다.
   * ★ 답보다 먼저 온다. 늦게 알면 답을 기다리는 사이 한 번 더 물었을 때 대화가 둘로 갈라진다.
   * ★★ `agentId` 는 **실제로 붙은 것**이다(판 E) — 고른 에이전트가 그새 중지됐으면 `null` 로
   *    온다. 화면은 «고른 것»이 아니라 이것을 따른다.
   */
  onConversation(id: string, agentId: string | null): void;
  /** 접수됐다 — 첫 청크. 「생각 중」을 띄우는 신호다. */
  onPing(): void;
  onDelta(text: string): void;
  /** 정상 종료 — 마스킹이 풀린 최종 글. */
  onComplete(text: string): void;
  /** 실패 · 중단. `cancelled` 면 사용자가 멈춘 것이다. */
  onError(message: string, cancelled: boolean): void;
  /**
   * 쓰기 도구가 **실행 전에** 멈추고 묻는다 (P1-#18).
   *
   * ★ 없으면 종전대로 오류 문구가 나간다 — 이 콜백을 안 준 화면은 승인 UI 가 없다는 뜻이다.
   */
  onConfirm?(c: AssistConfirm): void;
}

const ENDPOINT = '/api/v1/assist/turn';
const FALLBACK_ERROR = 'AI 응답을 받지 못했습니다.';

/** 서버가 내려주는 실패 코드 → 사람이 읽을 말. 모르는 코드는 일반 문구로 떨어진다. */
const ERROR_TEXT: Record<string, string> = {
  shared_secret_missing: 'AI 연동이 설정되지 않았습니다. 관리자에게 문의해 주세요.',
  fetch_failed: 'AI 서버에 닿지 못했습니다. 잠시 후 다시 시도해 주세요.',
  http_401: 'AI 연동 인증에 실패했습니다. 관리자에게 문의해 주세요.',
  http_429: '요청이 많습니다. 잠시 후 다시 시도해 주세요.',
  internal: FALLBACK_ERROR,
  /* 쓰기 도구가 사람 확인을 요구했다 — 확인 카드는 아직 이 패널에 없다(판 D 와 함께 온다).
     매달려 있지 않도록 서버가 여기서 끊고, 화면은 무엇을 해야 하는지 말한다. */
  confirm_required: '이 요청은 실행 전에 확인이 필요합니다. 아직 이 화면에서는 승인할 수 없어요 — 채팅에서 «/ai» 로 다시 물어봐 주세요.',
  stream_truncated: '답변이 도중에 끊겼습니다. 다시 시도해 주세요.',
};

/**
 * 한 턴을 시작한다. 되돌려 주는 함수를 부르면 **중단**된다.
 *
 * ★ 스트림을 «떼어 놓고» 곧바로 돌려준다 — 호출부가 중단 손잡이를 즉시 쥐어야 하기 때문이다.
 * ★ 실패는 던지지 않고 `onError` 로 간다. 화면이 말풍선 하나로 말하면 되는 일이라
 *   호출부가 try/catch 를 두 겹으로 쓰지 않게 한다.
 */
export function startAssistTurn(
  /**
   * ★★★ **앞 맥락을 «보내지 않는다»**(판 C). 판 B 에서는 화면이 지난 턴을 실어 보냈고 서버가
   *    그대로 프롬프트에 넣었다 — 사용자가 「AI 가 이렇게 말했다」를 지어낼 수 있었다.
   *    이제 대화 id 만 주고, 앞 맥락은 **서버가 제 DB 에서 읽는다.**
   */
  req: {
    text: string; conversationId?: string; model?: string; agentId?: string;
    /** 판 K — 사용자가 고른 연결 열쇠. 서버가 «회사가 지정한 것인지» 다시 확인한다. */
    choiceKey?: string;
    /**
     * 이 질문에 붙인 파일(2026-09-10) — **이미 올라간 것의 id 만** 보낸다.
     * ★ 서버가 권한을 다시 판정하고 «글»을 뽑아 질문 앞에 싣는다. 화면은 id 만 알면 된다.
     */
    attachments?: Array<{ fileId: string; name?: string }>;
  },
  cb: AssistCallbacks,
): () => void {
  const ac = new AbortController();
  void run(req, cb, ac);
  return () => ac.abort();
}

async function run(
  req: {
    text: string; conversationId?: string; model?: string; agentId?: string;
    choiceKey?: string; attachments?: Array<{ fileId: string; name?: string }>;
  },
  cb: AssistCallbacks,
  ac: AbortController,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(req),
      signal: ac.signal,
    });
  } catch {
    cb.onError(ac.signal.aborted ? '' : ERROR_TEXT.fetch_failed!, ac.signal.aborted);
    return;
  }

  if (!res.ok || !res.body) {
    // ★ 헤더를 쓰기 «전»의 실패는 진짜 상태코드로 온다 — 서버의 2단계 상태 규칙 덕이다.
    const detail = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const msg = typeof detail.message === 'string'
      ? detail.message
      : ERROR_TEXT[`http_${res.status}`] ?? `AI 요청이 거절되었습니다 (HTTP ${res.status}).`;
    cb.onError(msg, false);
    return;
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  // ⚠ 중단했을 때 본문 스트림이 알아서 끊긴다고 믿지 않는다 — 환경에 따라 다르다.
  //   안 끊기면 `read()` 가 영영 대기해 아래 «종단 보증»이 못 돈다.
  const onAbort = (): void => void reader.cancel().catch(() => {});
  ac.signal.addEventListener('abort', onAbort);

  let buffer = '';
  let settled = false;
  /** 확인 카드를 받았는가 — 종단 보증이 「빈 답」으로 오해하지 않게. */
  let sawConfirm = false;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += value;
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const parsed = parseBlock(block);
        if (!parsed || settled) continue;
        if (parsed.event === 'conversation') {
          const aid = parsed.data.agentId;
          cb.onConversation(String(parsed.data.id ?? ''), typeof aid === 'string' ? aid : null);
        }
        else if (parsed.event === 'ping') cb.onPing();
        else if (parsed.event === 'delta') cb.onDelta(String(parsed.data.text ?? ''));
        else if (parsed.event === 'complete') {
          settled = true;
          cb.onComplete(String(parsed.data.text ?? ''));
        } else if (parsed.event === 'confirm') {
          /* ★ 여기서 `settled` 로 두지 않는다 — 답이 아직 없고, 뒤이어 소켓이 정상 종료된다.
               종단 보증이 「빈 답」으로 오해하지 않도록 카드를 받았다는 것을 기억한다. */
          sawConfirm = true;
          const d = parsed.data;
          cb.onConfirm?.({
            tool: String(d.tool ?? ''),
            input: (d.input ?? {}) as Record<string, unknown>,
            whatWillHappen: String(d.whatWillHappen ?? ''),
            severity: String(d.severity ?? 'warn'),
            blastRadius: String(d.blastRadius ?? 'system'),
            continuationId: String(d.continuationId ?? ''),
            confirmToken: String(d.confirmToken ?? ''),
            expiresAt: String(d.expiresAt ?? ''),
          });
          if (!cb.onConfirm) {
            settled = true;
            cb.onError(ERROR_TEXT.confirm_required ?? FALLBACK_ERROR, false);
          }
        } else if (parsed.event === 'error') {
          settled = true;
          const code = String(parsed.data.code ?? '');
          cb.onError(ERROR_TEXT[code] ?? FALLBACK_ERROR, false);
        }
      }
    }
  } catch {
    /* 아래 종단 보증이 처리한다 */
  } finally {
    ac.signal.removeEventListener('abort', onAbort);
    void reader.cancel().catch(() => {});
  }

  /* ── 종단 보증 ──────────────────────────────────────────────────────────
     종단 프레임 없이 소켓이 끊기는 경로가 실재한다(프록시 절단 · 서버 재시작). 아무것도
     안 내면 화면은 「생각 중」에서 영영 멈춘다 — 오피스 AI 가 같은 보증을 갖고 있다. */
  /* ★ 확인 카드를 받은 턴은 **답이 없는 것이 정상**이다 — 여기서 오류를 내면
       카드를 그려 놓고 그 아래에 「실패했습니다」가 함께 뜬다. */
  if (!settled && !sawConfirm) cb.onError(ac.signal.aborted ? '' : FALLBACK_ERROR, ac.signal.aborted);
}

/**
 * 승인/거절을 보내고 **이어 가기**. 카드의 단추가 부른다.
 *
 * ★ 스트림 모양은 `startAssistTurn` 과 같다 — 화면이 같은 자리에 글을 이어 붙이면 된다.
 */
export async function continueAssistTurn(
  body: { continuationId: string; confirmToken: string; decision: 'approve' | 'deny'; conversationId?: string },
  cb: Pick<AssistCallbacks, 'onDelta' | 'onComplete' | 'onError'>,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${ENDPOINT}/continue`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(body),
    });
  } catch {
    cb.onError(ERROR_TEXT.fetch_failed!, false);
    return;
  }
  if (!res.ok || !res.body) {
    cb.onError(ERROR_TEXT[`http_${res.status}`] ?? FALLBACK_ERROR, false);
    return;
  }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  let settled = false;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += value;
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const parsed = parseBlock(block);
        if (!parsed || settled) continue;
        if (parsed.event === 'delta') cb.onDelta(String(parsed.data.text ?? ''));
        else if (parsed.event === 'complete') { settled = true; cb.onComplete(String(parsed.data.text ?? '')); }
        else if (parsed.event === 'error') {
          settled = true;
          cb.onError(ERROR_TEXT[String(parsed.data.code ?? '')] ?? FALLBACK_ERROR, false);
        }
      }
    }
  } catch { /* 아래 보증 */ } finally {
    void reader.cancel().catch(() => {});
  }
  if (!settled) cb.onError(FALLBACK_ERROR, false);
}

/** `event: <name>\ndata: <json>` 한 블록. 못 읽으면 null(그 블록만 버린다). */
function parseBlock(block: string): { event: string; data: Record<string, unknown> } | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return { event, data: {} };
  try {
    return { event, data: JSON.parse(dataLines.join('\n')) as Record<string, unknown> };
  } catch {
    return null;
  }
}

/** 확인 카드가 보여 줄 「받는 사람·제목·내용」 한 줄. 서버가 순서까지 정한다. */
export interface ConfirmPreviewLine { label: string; value: string }

/**
 * 「무엇이 일어나는가」를 **사람 말로** 받아 온다.
 *
 * ★ 실패해도 카드는 그려진다 — 미리보기는 **덤**이고, 없으면 도구 이름만 보인다.
 *   여기서 던지면 승인 자체를 못 하게 된다.
 */
export async function fetchConfirmPreview(
  tool: string, input: Record<string, unknown>,
): Promise<ConfirmPreviewLine[]> {
  try {
    const res = await fetch(`${ENDPOINT.replace('/turn', '')}/confirm/preview`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tool, input }),
    });
    if (!res.ok) return [];
    const d = (await res.json()) as { lines?: ConfirmPreviewLine[] };
    return Array.isArray(d.lines) ? d.lines : [];
  } catch {
    return [];
  }
}
